import { Badge, Button, Card, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconClipboardList, IconFileText, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { ContentCourse, VersionStatus } from '../../lib/types';
import { useMe } from '../../lib/me';
import { PageHeader } from '../../ui/PageHeader';
import { SystemStatus } from './Dashboard';
import { QueryState } from '../../ui/QueryState';

export const VERSION_STATUS: Record<VersionStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Черновик', color: 'gray' },
  ON_APPROVAL: { label: 'На одобрении', color: 'violet' },
  RETURNED: { label: 'Возвращена на доработку', color: 'orange' },
  PUBLISHED: { label: 'Опубликована', color: 'teal' },
  SUPERSEDED: { label: 'Заменена новой', color: 'gray' },
  ARCHIVED: { label: 'В архиве', color: 'dark' },
};

export function ContentCoursesPage() {
  const navigate = useNavigate();
  const me = useMe();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure(false);
  const [title, setTitle] = useState('');
  const [locale, setLocale] = useState('ru');
  const q = useQuery({ queryKey: ['content-courses'], queryFn: () => api.get<ContentCourse[]>('/admin/content/courses') });
  const create = useMutation({
    mutationFn: () => api.post<{ versionId: number }>('/admin/content/courses', { title, locale }),
    onSuccess: (r) => navigate(`/admin/content/versions/${r.versionId}`),
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });
  const newVersion = useMutation({
    mutationFn: (courseId: number) => api.post<{ versionId: number }>(`/admin/content/courses/${courseId}/versions`),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['content-courses'] });
      navigate(`/admin/content/versions/${r.versionId}`);
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  return (
    <>
      <PageHeader
        title="Контент: курсы"
        subtitle="Черновик → отправка на одобрение → одобрение вторым человеком с ролью «Контент» → публикация. Опубликованную версию менять нельзя: правка создаёт новую."
        actions={<Button leftSection={<IconPlus size={16} />} onClick={open}>Создать курс</Button>}
      />
      <QueryState loading={q.isLoading} error={q.error}>
        {() => (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {q.data!.map((c) => (
              <Card key={c.id} withBorder radius="lg" padding="lg" opacity={c.archived ? 0.6 : 1}>
                <Stack gap="xs" h="100%">
                  <Group justify="space-between" wrap="nowrap">
                    <Badge variant="dot" color="gray">{c.category || 'Без категории'} · {c.locale}</Badge>
                    {c.archived ? <Badge color="dark" variant="light">Снят с публикации</Badge> : c.published ? <Badge color="teal" variant="light">Опубликован v{c.published.version}</Badge> : <Badge color="gray" variant="light">Не опубликован</Badge>}
                  </Group>
                  <Title order={4}>{c.title}</Title>
                  <Text size="sm" c="dimmed" lineClamp={2}>{c.description || 'Без описания'}</Text>
                  {c.working && (
                    <Group gap={6}>
                      <Badge size="sm" color={VERSION_STATUS[c.working.status].color} variant="outline">v{c.working.version}: {VERSION_STATUS[c.working.status].label}</Badge>
                      <Group gap={4}><IconFileText size={14} color="gray" /><Text size="xs" c="dimmed">{c.working.lessons} ур.</Text></Group>
                      <Group gap={4}><IconClipboardList size={14} color="gray" /><Text size="xs" c="dimmed">{c.working.questions} вопр.</Text></Group>
                    </Group>
                  )}
                  <Text size="xs" c="dimmed" mt="auto">
                    Владелец: {c.owner ?? '-'}{c.published ? ` · опубликован ${fmtDate(c.published.publishedAt)}` : ''} · назначений {c.assignmentsCount}
                    {c.autoAssignDays ? ` · новым сотрудникам через ${c.autoAssignDays} дн.` : ''}
                  </Text>
                  <Group gap="xs">
                    {c.working ? (
                      <Button size="xs" onClick={() => navigate(`/admin/content/versions/${c.working!.id}`)}>Открыть v{c.working.version}</Button>
                    ) : (
                      !c.archived && <Button size="xs" variant="light" loading={newVersion.isPending} onClick={() => newVersion.mutate(c.id)}>Новая версия</Button>
                    )}
                    {c.published && <Button size="xs" variant="default" onClick={() => navigate(`/admin/content/versions/${c.published!.id}`)}>Опубликованная v{c.published.version}</Button>}
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </QueryState>
      {!me.roles.some((r) => r === 'ASSIGNMENTS' || r === 'RESULTS') && <div style={{ marginTop: 32 }}><SystemStatus /></div>}
      <Modal opened={opened} onClose={close} title="Новый курс" centered>
        <Stack>
          <TextInput label="Название" value={title} onChange={(e) => setTitle(e.currentTarget.value)} data-autofocus />
          <Select label="Язык курса" value={locale} onChange={(v) => setLocale(v ?? 'ru')} data={[{ value: 'ru', label: 'Русский' }, { value: 'en', label: 'English' }]} allowDeselect={false} />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>Отмена</Button>
            <Button disabled={!title.trim()} loading={create.isPending} onClick={() => create.mutate()}>Создать черновик</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
